// src/CATEGORIES/OperatorCategories.tsx
// ─────────────────────────────────────────────────────────────────────────────
// React WEB version of OperatorCategories
// Lucide React icons throughout.
//
// v2: CAR_BRANDS and LUXURY_CAR_BRANDS have been merged into a single
// CAR_BRANDS list. Every brand now carries a `classType` (economy /
// businessClass / luxury / ultraLuxury) instead of living in a separate
// "luxury" array. CLASS_TYPES and CAR_SIZES are new standalone picker
// lists (same shape/pattern as FUEL_TYPES / TRANSMISSION_TYPES) for use
// in listing forms — a brand's classType is just its sensible default,
// the host can still pick any class/size independently.
// ─────────────────────────────────────────────────────────────────────────────

import {
  Car, CarFront, Bus, Truck, Bike, Zap, Plane, Ship,
  Flame, Leaf, Settings, Settings2, Navigation,
  Box, Compass, Camera, Mountain, Waves,
  Star, Building2, Landmark, Radio, Gem, Crown, Briefcase,
} from "lucide-react";
import type { ReactNode } from "react";

/* ─────────────────────────────────────────────────────────────────────────── */
/* SHARED TYPES                                                                 */
/* ─────────────────────────────────────────────────────────────────────────── */

export interface CategoryItem {
  id:           number;
  name:         string;
  description?: string;
  parentId?:    number | null;
  color?:       string;
  icon:         ReactNode;
   image?: any;
}

/* A car brand also carries a default classType — the class it would
   normally be listed under unless the host overrides it. */
export interface CarBrandItem extends CategoryItem {
  classType: number; // one of CLASS_TYPES ids, see below
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* RIDE TYPES  (ids 320 – 325)                                                 */
/* Top-level "what kind of ride is this" selector — picked first, before the   */
/* more detailed CAR_TYPES / BIKE_TYPES / JET_TYPES list is shown. Use         */
/* vehicleTypesForRideType() below to get the right detail list to show next.  */
/* ─────────────────────────────────────────────────────────────────────────── */

export const RIDE_TYPES: CategoryItem[] = [
  { id: 320, name: "Car",     description: "Sedans, SUVs, vans, buses, trucks", color: "#CC0000", icon: <Car size={22} /> , image: "/icons/ride/car.png"     },
  { id: 321, name: "Bike",    description: "Motorbikes and bicycles",           color: "#1A1714", icon: <Bike size={22} />  ,image: "/icons/ride/bike.png"  },
  { id: 322, name: "Scooter", description: "Kick, electric & mobility scooters",color: "#0A4C8C", icon: <Zap size={22} />  ,image: "/icons/ride/scooter.png"   },
  { id: 323, name: "Boat",    description: "Boats, ferries, marine transport",  color: "#004D6E", icon: <Ship size={22} />  ,image: "/icons/ride/ship.png"  },
  { id: 324, name: "Jet",     description: "Aircraft, jets, helicopters",       color: "#0A3A70", icon: <Plane size={22} /> ,image: "/icons/ride/helicopter.png"  },
];

export const RIDE_TYPE = {
  CAR:     320,
  BIKE:    321,
  SCOOTER: 322,
  BOAT:    323,
  JET:     324,
} as const;

export const rideTypeName = (id: number): string =>
  RIDE_TYPES.find((r) => r.id === id)?.name ?? "Unknown";

/** The detailed vehicle-type list to show once a rideType has been picked. */
export const vehicleTypesForRideType = (rideType: number): CategoryItem[] => {
  switch (rideType) {
    case RIDE_TYPE.CAR:
      return CAR_TYPES;
    case RIDE_TYPE.BIKE:
      return BIKE_TYPES.filter((b) => b.id === 20 || b.id === 23 || b.id === 24); // Motorbike, E-Bicycle, Bicycle
    case RIDE_TYPE.SCOOTER:
      return BIKE_TYPES.filter((b) => b.id === 21 || b.id === 22); // Scooter, Electric Scooter
    case RIDE_TYPE.JET:
      return JET_TYPES;
    case RIDE_TYPE.BOAT:
      return []; // no dedicated boat-type list yet — add one if/when needed
    default:
      return [];
  }
};

/* ─────────────────────────────────────────────────────────────────────────── */
/* CAR TYPES  (ids 0 – 13)                                                     */
/* ─────────────────────────────────────────────────────────────────────────── */

export const CAR_TYPES: CategoryItem[] = [
  { id: 0,  name: "Sedan",          color: "#CC0000", icon: <Car size={22} /> },
  { id: 1,  name: "SUV",            color: "#991100", icon: <CarFront size={22} /> },
  { id: 2,  name: "Hatchback",      color: "#7A0000", icon: <Car size={22} /> },
  { id: 3,  name: "Coupe",          color: "#5A0000", icon: <Car size={22} /> },
  { id: 4,  name: "Convertible",    color: "#4A0000", icon: <Car size={22} /> },
  { id: 5,  name: "Station Wagon",  color: "#3A0000", icon: <Car size={22} /> },
  { id: 6,  name: "Pickup Truck",   color: "#3A2F00", icon: <Truck size={22} /> },
  { id: 7,  name: "Van",            color: "#2A2000", icon: <Bus size={22} /> },
  { id: 8,  name: "Minibus",        color: "#2A2A2A", icon: <Bus size={22} /> },
  { id: 9,  name: "Bus",            color: "#1A1A1A", icon: <Bus size={22} /> },
  { id: 10, name: "Truck / HGV",    color: "#4A3800", icon: <Truck size={22} /> },
  { id: 11, name: "Tuk-tuk",        color: "#5B4A00", icon: <Car size={22} /> },
  { id: 12, name: "Limousine",      color: "#0A0A0A", icon: <CarFront size={22} /> },
  { id: 13, name: "4x4 / Off-road", color: "#3A3000", icon: <CarFront size={22} /> },
];

export const carTypeName = (id: number): string =>
  CAR_TYPES.find((c) => c.id === id)?.name ?? "Unknown";

/* ─────────────────────────────────────────────────────────────────────────── */
/* CLASS TYPES  (ids 300 – 303)                                                */
/* Economy → Ultra Luxury. Selectable independently on a listing; a brand's    */
/* `classType` below is just the default it pre-selects.                      */
/* ─────────────────────────────────────────────────────────────────────────── */

export const CLASS_TYPES: CategoryItem[] = [
  { id: 300, name: "Economy",        description: "Everyday, budget-friendly rides",        color: "#3A4A2A", icon: <Car size={20} /> },
  { id: 301, name: "Business Class", description: "Comfort-focused, well-appointed",         color: "#0A4C8C", icon: <Briefcase size={20} /> },
  { id: 302, name: "Luxury",         description: "Premium marques and high-end trims",       color: "#5B4A00", icon: <Gem size={20} /> },
  { id: 303, name: "Ultra Luxury",   description: "Top-tier exotic and flagship vehicles",    color: "#1A1A1A", icon: <Crown size={20} /> },
];

export const CLASS_TYPE = {
  ECONOMY:         300,
  BUSINESS_CLASS:  301,
  LUXURY:          302,
  ULTRA_LUXURY:    303,
} as const;

export const classTypeName = (id: number): string =>
  CLASS_TYPES.find((c) => c.id === id)?.name ?? "Unknown";

/* ─────────────────────────────────────────────────────────────────────────── */
/* CAR SIZES  (ids 310 – 315)                                                  */
/* ─────────────────────────────────────────────────────────────────────────── */

export const CAR_SIZES: CategoryItem[] = [
  { id: 310, name: "XS",  description: "Extra small — city/micro cars",     icon: <Car size={18} /> },
  { id: 311, name: "S",   description: "Small — compact / hatchback",       icon: <Car size={20} /> },
  { id: 312, name: "M",   description: "Medium — mid-size sedan/SUV",       icon: <Car size={22} /> },
  { id: 313, name: "L",   description: "Large — full-size sedan/SUV",       icon: <CarFront size={24} /> },
  { id: 314, name: "XL",  description: "Extra large — van / minibus",       icon: <Bus size={24} /> },
  { id: 315, name: "XXL", description: "Extra extra large — bus / HGV",     icon: <Bus size={26} /> },
];

export const CAR_SIZE = {
  XS:  310,
  S:   311,
  M:   312,
  L:   313,
  XL:  314,
  XXL: 315,
} as const;

export const carSizeName = (id: number): string =>
  CAR_SIZES.find((s) => s.id === id)?.name ?? "Unknown";

/* ─────────────────────────────────────────────────────────────────────────── */
/* CAR BRANDS  (ids 100 – 154)                                                 */
/* One unified list — every brand carries a default classType instead of      */
/* living in a separate "luxury" array. Filter with carBrandsByClass() below. */
/* ─────────────────────────────────────────────────────────────────────────── */

export const CAR_BRANDS: CarBrandItem[] = [
  // ── mainstream / economy ──────────────────────────────────────────────
  { id: 100, name: "Toyota",          color: "#CC0000", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 101, name: "Honda",           color: "#CC0000", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 102, name: "Ford",            color: "#003A8C", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 103, name: "Chevrolet",       color: "#CC9900", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 104, name: "Volkswagen",      color: "#003366", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 105, name: "Hyundai",         color: "#002B5C", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 106, name: "Kia",             color: "#CC0000", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 107, name: "Nissan",          color: "#CC0000", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 108, name: "Mazda",           color: "#8B0000", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 109, name: "Subaru",          color: "#003A8C", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 110, name: "Mitsubishi",      color: "#CC0000", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 111, name: "Suzuki",          color: "#003366", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 112, name: "Renault",         color: "#FFCC00", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 113, name: "Peugeot",         color: "#1A1A1A", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 114, name: "Citroën",         color: "#CC0000", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 115, name: "Fiat",            color: "#CC0000", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 116, name: "Škoda",           color: "#006600", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 117, name: "SEAT",            color: "#CC0000", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 118, name: "Opel / Vauxhall", color: "#FFCC00", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 119, name: "Dacia",           color: "#003366", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 120, name: "Isuzu",           color: "#CC0000", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 121, name: "Tata",            color: "#003A8C", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 122, name: "Mahindra",        color: "#CC0000", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 123, name: "Geely",           color: "#003366", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 124, name: "BYD",             color: "#003A8C", icon: <Zap size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 125, name: "Chery",           color: "#CC0000", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 126, name: "MG",              color: "#CC0000", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 127, name: "Lada",            color: "#CC0000", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 128, name: "Bajaj",           color: "#CC6600", icon: <Bike size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 129, name: "Piaggio",         color: "#003366", icon: <Bike size={22} />, classType: CLASS_TYPE.ECONOMY },
  { id: 130, name: "Other",           color: "#666666", icon: <Car size={22} />, classType: CLASS_TYPE.ECONOMY },

  // ── luxury ─────────────────────────────────────────────────────────────
  { id: 140, name: "Mercedes-Benz", color: "#1A1A1A", icon: <CarFront size={22} />, classType: CLASS_TYPE.LUXURY },
  { id: 141, name: "BMW",           color: "#0066CC", icon: <CarFront size={22} />, classType: CLASS_TYPE.LUXURY },
  { id: 142, name: "Audi",          color: "#1A1A1A", icon: <CarFront size={22} />, classType: CLASS_TYPE.LUXURY },
  { id: 143, name: "Lexus",         color: "#1A1A1A", icon: <CarFront size={22} />, classType: CLASS_TYPE.LUXURY },
  { id: 144, name: "Porsche",       color: "#CC0000", icon: <CarFront size={22} />, classType: CLASS_TYPE.LUXURY },
  { id: 145, name: "Jaguar",        color: "#1A1A1A", icon: <CarFront size={22} />, classType: CLASS_TYPE.LUXURY },
  { id: 146, name: "Land Rover",    color: "#1A3A1A", icon: <CarFront size={22} />, classType: CLASS_TYPE.LUXURY },
  { id: 147, name: "Cadillac",      color: "#1A1A1A", icon: <CarFront size={22} />, classType: CLASS_TYPE.LUXURY },
  { id: 148, name: "Lincoln",       color: "#1A1A1A", icon: <CarFront size={22} />, classType: CLASS_TYPE.LUXURY },
  { id: 149, name: "Genesis",       color: "#1A1A1A", icon: <CarFront size={22} />, classType: CLASS_TYPE.LUXURY },
  { id: 150, name: "Infiniti",      color: "#1A1A1A", icon: <CarFront size={22} />, classType: CLASS_TYPE.LUXURY },
  { id: 151, name: "Acura",         color: "#CC0000", icon: <CarFront size={22} />, classType: CLASS_TYPE.LUXURY },
  { id: 152, name: "Volvo",         color: "#003A8C", icon: <CarFront size={22} />, classType: CLASS_TYPE.LUXURY },
  { id: 153, name: "Tesla",         color: "#CC0000", icon: <Zap size={22} />,       classType: CLASS_TYPE.LUXURY },
  { id: 154, name: "Alfa Romeo",    color: "#CC0000", icon: <CarFront size={22} />, classType: CLASS_TYPE.LUXURY },
  { id: 155, name: "Other Luxury",  color: "#666666", icon: <CarFront size={22} />, classType: CLASS_TYPE.LUXURY },

  // ── ultra luxury / exotic ────────────────────────────────────────────────
  { id: 160, name: "Rolls-Royce",  color: "#1A1A1A", icon: <CarFront size={22} />, classType: CLASS_TYPE.ULTRA_LUXURY },
  { id: 161, name: "Bentley",      color: "#1A4A1A", icon: <CarFront size={22} />, classType: CLASS_TYPE.ULTRA_LUXURY },
  { id: 162, name: "Ferrari",      color: "#CC0000", icon: <CarFront size={22} />, classType: CLASS_TYPE.ULTRA_LUXURY },
  { id: 163, name: "Lamborghini",  color: "#CC9900", icon: <CarFront size={22} />, classType: CLASS_TYPE.ULTRA_LUXURY },
  { id: 164, name: "Maserati",     color: "#003A8C", icon: <CarFront size={22} />, classType: CLASS_TYPE.ULTRA_LUXURY },
  { id: 165, name: "Aston Martin", color: "#006633", icon: <CarFront size={22} />, classType: CLASS_TYPE.ULTRA_LUXURY },
  { id: 166, name: "McLaren",      color: "#FF6600", icon: <CarFront size={22} />, classType: CLASS_TYPE.ULTRA_LUXURY },
  { id: 167, name: "Bugatti",      color: "#003A8C", icon: <CarFront size={22} />, classType: CLASS_TYPE.ULTRA_LUXURY },
  { id: 168, name: "Maybach",      color: "#1A1A1A", icon: <CarFront size={22} />, classType: CLASS_TYPE.ULTRA_LUXURY },
];

export const carBrandName = (id: number): string =>
  CAR_BRANDS.find((b) => b.id === id)?.name ?? "Unknown";

/** All brands whose default classType matches the given CLASS_TYPE id. */
export const carBrandsByClass = (classType: number): CarBrandItem[] =>
  CAR_BRANDS.filter((b) => b.classType === classType);

/* ─────────────────────────────────────────────────────────────────────────── */
/* BIKE TYPES  (ids 20 – 24)                                                   */
/* ─────────────────────────────────────────────────────────────────────────── */

export const BIKE_TYPES: CategoryItem[] = [
  { id: 20, name: "Motorbike",        color: "#1A1714", icon: <Bike size={22} /> },
  { id: 21, name: "Scooter",          color: "#2A2720", icon: <Bike size={22} /> },
  { id: 22, name: "Electric Scooter", color: "#0A4C8C", icon: <Zap size={22} />  },
  { id: 23, name: "Electric Bicycle", color: "#2A4A2A", icon: <Zap size={22} />  },
  { id: 24, name: "Bicycle",          color: "#3A4A2A", icon: <Bike size={22} /> },
];

export const bikeTypeName = (id: number): string =>
  BIKE_TYPES.find((b) => b.id === id)?.name ?? "Unknown";

/* ─────────────────────────────────────────────────────────────────────────── */
/* JET / AIRCRAFT TYPES  (ids 30 – 35)                                        */
/* ─────────────────────────────────────────────────────────────────────────── */

export const JET_TYPES: CategoryItem[] = [
  { id: 30, name: "Light Aircraft", color: "#0A4C8C", icon: <Plane size={22} /> },
  { id: 31, name: "Private Jet",    color: "#0A3A70", icon: <Plane size={22} /> },
  { id: 32, name: "Helicopter",     color: "#082C56", icon: <Plane size={22} /> },
  { id: 33, name: "Seaplane",       color: "#004D6E", icon: <Ship size={22} />  },
  { id: 34, name: "Drone (cargo)",  color: "#1A1A3A", icon: <Radio size={22} /> },
  { id: 35, name: "Ultralight",     color: "#0A5C9C", icon: <Plane size={22} /> },
];

export const jetTypeName = (id: number): string =>
  JET_TYPES.find((j) => j.id === id)?.name ?? "Unknown";

/* ─────────────────────────────────────────────────────────────────────────── */
/* FUEL TYPES  (ids 40 – 46)                                                   */
/* ─────────────────────────────────────────────────────────────────────────── */

export const FUEL_TYPES: CategoryItem[] = [
  { id: 40, name: "Petrol",    color: "#CC0000", icon: <Flame size={22} /> },
  { id: 41, name: "Diesel",    color: "#4A3800", icon: <Flame size={22} /> },
  { id: 42, name: "Electric",  color: "#0A4C8C", icon: <Zap size={22} />   },
  { id: 43, name: "Hybrid",    color: "#2A4A2A", icon: <Leaf size={22} />  },
  { id: 44, name: "Gas (LPG)", color: "#5B4A00", icon: <Flame size={22} /> },
  { id: 45, name: "Jet Fuel",  color: "#0A3A70", icon: <Plane size={22} /> },
  { id: 46, name: "Marine",    color: "#004D6E", icon: <Ship size={22} />  },
];

export const fuelTypeName = (id: number): string =>
  FUEL_TYPES.find((f) => f.id === id)?.name ?? "Unknown";

/* ─────────────────────────────────────────────────────────────────────────── */
/* TRANSMISSION TYPES  (ids 50 – 51)                                           */
/* ─────────────────────────────────────────────────────────────────────────── */

export const TRANSMISSION_TYPES: CategoryItem[] = [
  { id: 50, name: "Automatic", color: "#1A1714", icon: <Settings size={22} />  },
  { id: 51, name: "Manual",    color: "#1A1714", icon: <Settings2 size={22} /> },
];

export const transmissionName = (id: number): string =>
  TRANSMISSION_TYPES.find((t) => t.id === id)?.name ?? "Unknown";

/* ─────────────────────────────────────────────────────────────────────────── */
/* OPERATOR ROLES  (ids 1, 3)                                                  */
/* ─────────────────────────────────────────────────────────────────────────── */

export const OPERATOR_ROLES: CategoryItem[] = [
  {
    id:          1,
    name:        "Driver",
    description: "Operates road vehicles — cars, taxis, buses, lorries, motorcycles",
    color:       "#CC0000",
    icon:        <Car size={22} />,
  },
  {
    id:          3,
    name:        "Tour Guide",
    description: "Leads guided excursions — safari, city tours, hiking, diving",
    color:       "#5B4A00",
    icon:        <Compass size={22} />,
  },
];

export const operatorRoleName = (id: number): string =>
  OPERATOR_ROLES.find((r) => r.id === id)?.name ?? "Unknown";

/* ─────────────────────────────────────────────────────────────────────────── */
/* BUSINESS TYPES  (ids 10 – 11)                                               */
/* ─────────────────────────────────────────────────────────────────────────── */

export const BUSINESS_TYPES: CategoryItem[] = [
  {
    id:          10,
    name:        "Ride Hailing",
    description: "On-demand passenger rides",
    color:       "#CC0000",
    icon:        <Navigation size={22} />,
  },
  {
    id:          11,
    name:        "Parcel Delivery",
    description: "Last-mile parcel and courier delivery",
    color:       "#3A2F00",
    icon:        <Box size={22} />,
  },
];

export const businessTypeName = (id: number): string =>
  BUSINESS_TYPES.find((b) => b.id === id)?.name ?? "Unknown";

/* ─────────────────────────────────────────────────────────────────────────── */
/* TOUR GUIDE SUBTYPES  (ids 60 – 63)                                          */
/* ─────────────────────────────────────────────────────────────────────────── */

export const TOUR_GUIDE_TYPES: CategoryItem[] = [
  {
    id:          60,
    name:        "Safari Guide",
    description: "Wildlife game drives and bush experiences",
    color:       "#5B4A00",
    icon:        <Leaf size={22} />,
  },
  {
    id:          61,
    name:        "City Tour Guide",
    description: "Guided urban sightseeing and excursions",
    color:       "#3A3000",
    icon:        <Camera size={22} />,
  },
  {
    id:          62,
    name:        "Hiking Guide",
    description: "Guided nature hikes and mountain treks",
    color:       "#2A4A2A",
    icon:        <Mountain size={22} />,
  },
  {
    id:          63,
    name:        "Diving Instructor",
    description: "Scuba, snorkelling, water sports",
    color:       "#004D5A",
    icon:        <Waves size={22} />,
  },
];

export const tourGuideTypeName = (id: number): string =>
  TOUR_GUIDE_TYPES.find((t) => t.id === id)?.name ?? "Unknown";

/* ─────────────────────────────────────────────────────────────────────────── */
/* COMPANY TYPES  (plain strings)                                               */
/* ─────────────────────────────────────────────────────────────────────────── */

export const COMPANY_TYPES: string[] = [
  "individual",
  "company",
  "nonprofit",
  "government",
];

/*
  ID RANGE REFERENCE
  ──────────────────
   0 –  13   CAR_TYPES
  20 –  24   BIKE_TYPES
  30 –  35   JET_TYPES
  40 –  46   FUEL_TYPES
  50 –  51   TRANSMISSION_TYPES
   1,  3     OPERATOR_ROLES
  10 –  11   BUSINESS_TYPES
  60 –  63   TOUR_GUIDE_TYPES
 100 – 130   CAR_BRANDS · economy
 140 – 155   CAR_BRANDS · luxury
 160 – 168   CAR_BRANDS · ultra luxury
 300 – 303   CLASS_TYPES  (economy / businessClass / luxury / ultraLuxury)
 310 – 315   CAR_SIZES    (XS / S / M / L / XL / XXL)
 320 – 324   RIDE_TYPES   (Car / Bike / Scooter / Boat / Jet)

  BREAKING CHANGE from v1:
  ────────────────────────
  LUXURY_CAR_BRANDS and LUXURY_CAR_TYPES have been removed. Luxury brands
  now live inside CAR_BRANDS (ids re-numbered 140+ / 160+) and carry a
  `classType` field instead. If anything still imports
  LUXURY_CAR_BRANDS / luxuryCarBrandName / LUXURY_CAR_TYPES, switch it to
  CAR_BRANDS + carBrandsByClass(CLASS_TYPE.LUXURY) (or ULTRA_LUXURY).
*/